import { API_BASE } from "../services/apiConfig.js";
import AccountSync from "../services/AccountSync.js";
import StorageService from "../services/StorageService.js";
import DailyChallenge from "../games/DailyChallenge.js";
import { formatDuration } from "../utils/TimeUtils.js";
import { VISIBLE_GROUPS } from "../utils/gameGroups.js";

const FEATURED_GROUP = "global";
import { RESUMABLE_MODES } from "../utils/gameCatalog.js";
import { rankBadge, escapeHtml, avatarHtml, renderHearts, renderProfileHeader, renderProfileStats } from "../utils/PlayerProfile.js";
import { PICTO_FLAME } from "../utils/Pictos.js";
import { msUntilWeekEnd } from "../utils/WeekUtils.js";

/**
 * Couleur du segment actif dans la pilule des modes : celle du mode sur les
 * cards de la home (`.btn.classic` & co, `app/scss/partials/_buttons.scss`),
 * mêmes noms de classe que ceux de la page Scores. Déduite du `modeKey` plutôt
 * que déclarée sur chaque mode, sinon un mode ajouté à `GAME_GROUPS` sort du
 * code sans couleur. Un modeKey absent de la table retombe sur le bleu.
 */
const MODE_CLASSES = {
    "home.classic":  "classic",
    "home.hard":     "hard",
    "home.speedRun": "speed-run",
    "home.reverse":  "reverse",
    "home.tricky":   "tricky",
};

/**
 * Fraîcheur d'un record, en relatif court. `null` pour les runs d'avant
 * l'enregistrement de la date : on n'affiche rien plutôt que d'inventer.
 */
function formatAge(timestamp, l) {
    if (!timestamp) return "";
    const days = Math.floor((Date.now() - timestamp) / 86400000);
    if (days <= 0) return l.t("leaderboard.today");
    if (days === 1) return l.t("leaderboard.yesterday");
    if (days < 30) return l.t("leaderboard.daysAgo").replace("{n}", days);
    const months = Math.floor(days / 30);
    return l.t("leaderboard.monthsAgo").replace("{n}", months);
}

export default class Leaderboard {
    constructor() {
        this.page = document.querySelector(".page.leaderboard");
        if (!this.page) return;
        if (Leaderboard.redirectLegacyMineHash()) return;

        this.gameTabs = this.page.querySelector(".leaderboard-game-tabs");
        this.modeTabs = this.page.querySelector(".leaderboard-mode-tabs");
        this.playCta = this.page.querySelector(".leaderboard-play");
        this.body = this.page.querySelector(".leaderboard-body");
        this.lang = geoNerdApp.languageManager;
        this.activeGroup = VISIBLE_GROUPS[0];
        this.activeMode = VISIBLE_GROUPS[0].modes[0];
        this.cache = {};
        // « Depuis toujours » ou « Cette semaine » (48 du TODO), pour les modes qui se jouent en parties.
        this.period = "all";
        this.me = null;

        this.initProfilePopup();

        // `AccountSync.init()` (idempotent) résout après le pull ET le push de la
        // sauvegarde : avant lui on ne connaît pas le joueur, et le score de la partie
        // qu'il vient de terminer n'est pas encore remonté, et le classement chargé dans
        // la foulée l'ignorerait. On repart donc de zéro une fois la synchro faite.
        AccountSync.init().then(() => {
            this.me = AccountSync.user?.username || null;
            this.cache = {};
            this.load(this.activeMode.id);
        });

        const hash = window.location.hash.slice(1);
        if (hash) {
            for (const group of VISIBLE_GROUPS) {
                const mode = group.modes.find(m => m.id === hash);
                if (mode) { this.activeGroup = group; this.activeMode = mode; break; }
            }
        }

        this.renderGameTabs();
        this.renderModeTabs();
        this.updateHash();
        this.load(this.activeMode.id);

        window.addEventListener("hashchange", () => this.onHashChange());
    }

    /**
     * La progression personnelle vivait ici sous `#mine` (ex-page /scores) ; elle
     * a sa propre page depuis. Les liens partagés d'alors restent en circulation :
     * on les y emmène plutôt que de les laisser sur un onglet qui n'existe plus.
     */
    static redirectLegacyMineHash() {
        if (window.location.hash !== "#mine") return false;
        const lang = window.geoNerdApp.languageManager;
        window.location.replace(lang.currentLang === "en" ? "/en/profile" : "/profil");
        return true;
    }

    onHashChange() {
        const hash = window.location.hash.slice(1);
        if (!hash || hash === this.activeMode.id) return;

        for (const group of VISIBLE_GROUPS) {
            const mode = group.modes.find(m => m.id === hash);
            if (mode) {
                this.activeGroup = group;
                this.activeMode = mode;
                this.renderGameTabs();
                this.renderModeTabs();
                this.load(this.activeMode.id);
                break;
            }
        }
    }

    updateHash() {
        history.replaceState(null, "", `#${this.activeMode.id}`);
    }

    /**
     * Le classement global (score composite, tous les jeux) passe en tête et se
     * distingue des autres onglets : c'est le seul qui compare tous les joueurs.
     */
    renderGameTabs() {
        const l = this.lang;
        const tabs = [...VISIBLE_GROUPS.filter(g => g.id === FEATURED_GROUP), ...VISIBLE_GROUPS.filter(g => g.id !== FEATURED_GROUP)];
        this.gameTabs.innerHTML = tabs.map(g => {
            const active = g.id === this.activeGroup.id ? " active" : "";
            const featured = g.id === FEATURED_GROUP ? " is-featured" : "";
            return `<button class="lb-game-tab${featured}${active}" data-group="${g.id}">
                <img class="lb-tab-icon" src="${g.icon}" alt="">${l.t(g.nameKey)}
            </button>`;
        }).join("");

        this.gameTabs.querySelectorAll(".lb-game-tab").forEach(btn => {
            btn.addEventListener("click", () => {
                this.activeGroup = VISIBLE_GROUPS.find(g => g.id === btn.dataset.group);
                this.activeMode = this.activeGroup.modes[0];
                this.gameTabs.querySelectorAll(".lb-game-tab").forEach(b => b.classList.remove("active"));
                btn.classList.add("active");
                this.renderModeTabs();
                this.updateHash();
                this.load(this.activeMode.id);
            });
        });
    }

    /**
     * Les modes sont rendus dans une seule pilule (contrôle segmenté), de 1 segment
     * (Score global) à 4 (Capital Nerd). Un groupe à mode unique n'offre aucun
     * choix : la pilule y sert d'étiquette, sans segment rempli (`is-single`).
     */
    renderModeTabs() {
        const l = this.lang;
        const modes = this.activeGroup.modes;
        this.modeTabs.classList.toggle("is-single", modes.length === 1);
        this.modeTabs.innerHTML = modes.map(m => {
            const active = m.id === this.activeMode.id;
            const color = MODE_CLASSES[m.modeKey] || "";
            return `<button type="button" class="lb-mode-tab ${color}${active ? " active" : ""}" role="tab" aria-selected="${active}" data-mode="${m.id}">${l.t(m.modeKey)}</button>`;
        }).join("");

        this.modeTabs.querySelectorAll(".lb-mode-tab").forEach(btn => {
            btn.addEventListener("click", () => {
                this.activeMode = this.activeGroup.modes.find(m => m.id === btn.dataset.mode);
                this.setActiveModeTab(btn);
                this.updateHash();
                this.load(this.activeMode.id);
            });
        });
    }

    /** L'état actif porte aussi `aria-selected` : le nav est un `role="tablist"`. */
    setActiveModeTab(active) {
        this.modeTabs.querySelectorAll(".lb-mode-tab").forEach(b => {
            b.classList.toggle("active", b === active);
            b.setAttribute("aria-selected", String(b === active));
        });
    }

    /**
     * Bouton d'accès direct au mode affiché, en dehors du corps re-rendu à
     * chaque `load()` (voir `renderResume()`, conditionné à une partie en
     * cours) : le classement n'ouvrait jusque-là aucune page de jeu, seul un
     * détour par le menu permettait de jouer au mode qu'on est en train de
     * regarder. Absent sur le score composite, qui n'a pas de page propre
     * (voir le commentaire sur `slug` dans `gameGroups.js`).
     */
    renderPlayCta() {
        if (!this.playCta) return;
        const slug = this.activeMode?.slug;
        if (!slug) {
            this.playCta.innerHTML = "";
            return;
        }
        const l = this.lang;
        this.playCta.innerHTML = `<a href="${this.href(slug)}" class="btn lb-play-btn">${escapeHtml(l.t("leaderboard.playCta"))}</a>`;
    }

    /**
     * Les modes joués en parties ont aussi un classement de la semaine (remis à
     * zéro le lundi à 00:00 UTC) : avec des dizaines de joueurs à 193/193, le
     * classement de tous les temps ne laissait aucune chance à un nouveau venu.
     * Miroir de `WEEKLY_GAME_IDS` côté serveur.
     */
    static supportsWeek(mode) {
        return Boolean(mode) && (mode.type === "classic" || mode.type === "speedrun" || mode.id === "usstateslist");
    }

    currentPeriod() {
        return Leaderboard.supportsWeek(this.activeMode) ? this.period : "all";
    }

    async load(gameId) {
        this.renderPlayCta();
        const period = this.currentPeriod();
        const cacheKey = `${gameId}:${period}`;
        if (this.cache[cacheKey]) {
            this.render(this.activeMode, this.cache[cacheKey]);
            return;
        }
        this.body.innerHTML = `<p class="lb-loading">${this.lang.t("leaderboard.loading")}</p>`;
        try {
            const query = period === "week" ? "&period=week" : "";
            const res = await fetch(`${API_BASE}/leaderboard?game=${encodeURIComponent(gameId)}${query}`);
            if (!res.ok) throw new Error(res.status);
            const { entries } = await res.json();
            this.cache[cacheKey] = entries;
            this.render(this.activeMode, entries);
        } catch {
            this.body.innerHTML = `<p class="lb-error">${this.lang.t("leaderboard.errorLoad")}</p>`;
        }
    }

    /** Bascule « Depuis toujours / Cette semaine », et la fin de la semaine en cours. */
    renderPeriodToggle(mode) {
        if (!Leaderboard.supportsWeek(mode)) return "";
        const l = this.lang;
        const period = this.currentPeriod();
        const button = (value, key) => `<button type="button" class="lb-period-btn${period === value ? " active" : ""}" role="tab" aria-selected="${period === value}" data-period="${value}">${l.t(key)}</button>`;
        const hours = Math.ceil(msUntilWeekEnd() / 3600000);
        const ends = hours >= 24
            ? l.t("leaderboard.weekEndsDays").replace("{d}", Math.floor(hours / 24)).replace("{h}", hours % 24)
            : l.t("leaderboard.weekEndsHours").replace("{h}", hours);
        return `
            <div class="lb-period" role="tablist">
                ${button("all", "leaderboard.periodAll")}
                ${button("week", "leaderboard.periodWeek")}
            </div>
            ${period === "week" ? `<p class="lb-period-note">${ends}</p>` : ""}`;
    }

    bindPeriodToggle() {
        this.body.querySelectorAll(".lb-period-btn").forEach(btn => {
            btn.addEventListener("click", () => {
                if (btn.dataset.period === this.currentPeriod()) return;
                this.period = btn.dataset.period;
                this.load(this.activeMode.id);
            });
        });
    }

    /** Top 3 podium (silver, gold, bronze order) shown above the full table. */
    renderPodium(mode, entries) {
        const type = mode?.type;

        const steps = [1, 0, 2]
            .map(i => entries[i])
            .filter(Boolean)
            .map(e => {
                const scoreText = mode.total ? `${e.score}/${mode.total}` : e.score;
                const mine = e.username === this.me ? " is-me" : "";
                return `
                    <div class="lb-podium-step rank-${e.rank}${mine}">
                        <div class="lb-podium-avatar">${avatarHtml(e, 64)}</div>
                        <div class="lb-podium-medal">${rankBadge(e.rank)}</div>
                        <button type="button" class="lb-podium-name" data-profile-username="${escapeHtml(e.username)}" title="${escapeHtml(e.username)}">${escapeHtml(e.username)}</button>
                        <div class="lb-podium-block">
                            <div class="lb-podium-score">${scoreText}</div>
                            ${this.podiumTiebreak(type, e)}
                        </div>
                    </div>
                `;
            }).join("");
        return `<div class="lb-podium lb-podium--${type}">${steps}</div>`;
    }

    /**
     * Ce qui départage réellement les ex aequo du podium. Sans ça, trois joueurs à
     * 193/193 y paraissent identiques alors que l'ordre se joue sur les vies et le
     * temps : l'information est dans le tableau, mais pas sous les yeux du visiteur.
     */
    podiumTiebreak(type, e) {
        const l = this.lang;
        if (type === "daily") {
            const streak = e.streak ? `${PICTO_FLAME}${e.streak}` : "";
            return `<div class="lb-podium-detail">${[streak, formatDuration(e.duration)].filter(Boolean).join(" · ")}</div>`;
        }
        if (type === "speedrun") {
            return e.errors == null ? "" : `<div class="lb-podium-detail">${e.errors} ${l.t("leaderboard.errors").toLowerCase()}</div>`;
        }
        if (type === "letters") {
            // Le podium n'affiche que le temps : sur Country Nerd, tout le monde y est
            // à 24/24 avec les 193 pays, et la ligne « x pays trouvés · temps » débordait
            // de la marche. Le détail complet reste dans le tableau.
            return `<div class="lb-podium-detail"><span class="lb-podium-time">${this.timeText(e, false)}</span></div>`;
        }
        if (type === "classic") {
            const hearts = renderHearts(e.errors, n => l.t("leaderboard.livesLeft").replace("{n}", n));
            return `<div class="lb-podium-detail">${hearts}<span class="lb-podium-time">${this.timeText(e, false)}</span></div>`;
        }
        return "";
    }

    /**
     * Temps affiché. Un run jamais mesuré n'affiche pas de durée inventée mais un
     * tiret, suivi d'un « i » qui déplie l'explication : le score est antérieur à
     * l'enregistrement du temps.
     */
    timeText(e, withInfo = true) {
        const text = formatDuration(e.duration);
        if (!e.untimed || !withInfo) return text;
        const label = escapeHtml(this.lang.t("leaderboard.legacyInfoLabel"));
        return `${text}<button type="button" class="lb-info" aria-label="${label}" title="${label}" aria-expanded="false">i</button>`;
    }

    render(mode, entries) {
        const type = mode?.type;
        const l = this.lang;

        const compositeNote = type === "composite"
            ? `<p class="lb-composite-note">${l.t("leaderboard.compositeExplanation")}</p>`
            : "";
        const resumeHtml = this.renderResume(mode);

        const periodHtml = this.renderPeriodToggle(mode);

        if (!entries || entries.length === 0) {
            const empty = this.currentPeriod() === "week" ? "leaderboard.emptyWeek" : "leaderboard.empty";
            this.body.innerHTML = `
                ${periodHtml}
                ${compositeNote}
                ${resumeHtml}
                <p class="lb-empty">${l.t(empty)}</p>
            `;
            this.bindPeriodToggle();
            return;
        }

        const isSimple = type === "exploration" || type === "composite";
        const isLetters = type === "letters";
        const isDaily  = type === "daily";
        const isSpeedRun = type === "speedrun";

        const scoreCell = (e) => {
            const display = mode.total ? `${e.score}<span class="lb-total">/${mode.total}</span>` : e.score;
            const avgHtml = e.avgScore != null && (type === "classic" || type === "daily" || type === "speedrun")
                ? `<small class="lb-avg-score">${l.t("leaderboard.avgScore")} ${e.avgScore}</small>`
                : "";
            // Country Nerd : les pays trouvés sont un second niveau de lecture du score
            // (24 lettres, mais 193 pays), pas une colonne à part entière.
            const foundHtml = isLetters && e.found != null
                ? `<small class="lb-avg-score">${e.found} ${l.t("leaderboard.countriesFound").toLowerCase()}</small>`
                : "";
            return `<td class="lb-score">${display}${avgHtml}${foundHtml}</td>`;
        };

        const header = isSimple
            ? `<th>${l.t("leaderboard.score")}</th>`
            : isLetters
            ? `<th>${l.t("leaderboard.score")}</th><th>${l.t("leaderboard.errors")}</th><th>${l.t("leaderboard.time")}</th>`
            : isDaily
            ? `<th>${l.t("leaderboard.score")}</th><th>${l.t("leaderboard.currentStreak")}</th><th>${l.t("leaderboard.time")}</th>`
            : isSpeedRun
            ? `<th>${l.t("leaderboard.score")}</th><th>${l.t("leaderboard.errors")}</th>`
            : `<th>${l.t("leaderboard.score")}</th><th>${l.t("leaderboard.lives")}</th><th>${l.t("leaderboard.time")}</th>`;

        const rows = entries.map(e => {
            const extra = isSimple ? ""
                : isLetters ? `<td class="lb-errors">${e.errors ?? "–"}</td><td class="lb-time">${this.timeText(e)}</td>`
                : isDaily  ? `<td class="lb-streak">${e.streak ? `${PICTO_FLAME}${e.streak}` : "–"}</td><td class="lb-time">${formatDuration(e.duration)}</td>`
                : isSpeedRun ? `<td class="lb-errors">${e.errors ?? "–"}</td>`
                : `<td class="lb-lives">${renderHearts(e.errors, n => l.t("leaderboard.livesLeft").replace("{n}", n))}</td><td class="lb-time">${this.timeText(e)}</td>`;
            const isMine = e.username === this.me;
            const mine = isMine ? " is-me" : "";
            const badge = isMine ? `<span class="lb-you-badge">${l.t("leaderboard.you")}</span>` : "";
            const age = formatAge(e.recordedAt, l);
            const ageHtml = age ? `<small class="lb-age">${age}</small>` : "";
            return `<tr class="lb-row rank-${e.rank}${mine}"${mine ? ' id="lb-my-row"' : ""} data-profile-username="${escapeHtml(e.username)}">
                <td class="lb-rank">${rankBadge(e.rank)}</td>
                <td class="lb-player" title="${escapeHtml(e.username)}">
                    <button type="button" class="lb-player-link" data-profile-username="${escapeHtml(e.username)}">
                        <span class="lb-avatar">${avatarHtml(e, 34)}</span><span class="lb-player-name">${escapeHtml(e.username)}${badge}${ageHtml}</span>
                    </button>
                </td>
                ${scoreCell(e)}${extra}
            </tr>`;
        }).join("");

        this.body.innerHTML = `
            ${periodHtml}
            ${compositeNote}
            ${resumeHtml}
            ${this.renderPodium(mode, entries)}
            ${this.renderMyStanding(mode, entries)}
            <table class="lb-table">
                <caption class="sr-only">${l.t("leaderboard.title")} : ${l.t(mode.modeKey)}</caption>
                <thead><tr>
                    <th>${l.t("leaderboard.rank")}</th>
                    <th>${l.t("leaderboard.player")}</th>
                    ${header}
                </tr></thead>
                <tbody>${rows}</tbody>
            </table>
            <p class="lb-legacy-note" hidden><span class="lb-info is-static" aria-hidden="true">i</span>${l.t("leaderboard.legacyScoreNote")}</p>
        `;

        this.bindMyStanding();
        this.bindLegacyInfo();
        this.bindPeriodToggle();
    }

    /**
     * Bandeau « reprendre la partie en cours », quand le mode affiché en a une.
     * Purement local (une partie en cours n'est jamais synchronisée) : affiché
     * qu'on soit connecté ou non, contrairement au bandeau « où j'en suis ».
     */
    renderResume(mode) {
        const resumable = this.findResumable(mode?.id);
        if (!resumable) return "";
        const l = this.lang;
        return `<p class="lb-resume">
            <span class="lb-resume-label">${l.t("leaderboard.resumeLabel")}</span>
            <a href="${this.href(resumable.slug)}" class="btn lb-resume-btn">${escapeHtml(l.t("inProgress.resume"))}</a>
        </p>`;
    }

    /** Même correspondance `gameId` que `/parties-en-cours` (`InProgress.js`). */
    findResumable(gameId) {
        const mode = RESUMABLE_MODES.find(m => m.gameId === gameId);
        if (!mode) return null;
        if (mode.kind === "pool" && this.hasPoolRun(mode)) return mode;
        if (mode.kind === "letters" && this.hasLettersRun(mode)) return mode;
        if (mode.kind === "daily" && this.hasDailyRun(mode)) return mode;
        return null;
    }

    /** Même logique que `InProgress.scanPool()`, réduite à un booléen. */
    hasPoolRun(mode) {
        const left = StorageService.load(`${mode.gameId}.countriesleft`);
        const remaining = Array.isArray(left) ? left.length : null;
        const lives = mode.noLives ? undefined : (() => {
            const lifes = StorageService.load(`${mode.gameId}.lifes`);
            return lifes === null ? 3 : parseInt(lifes, 10);
        })();

        if (remaining === 0 || (lives !== undefined && lives <= 0)) return false;
        return !(remaining === null && (mode.noLives || lives === 3));
    }

    /** Même logique que `InProgress.scanLetters()` : une seule lettre entamée suffit. */
    hasLettersRun(mode) {
        const letters = window.geoNerdApp.letters || [];
        return letters.some((letter) => {
            const startedAt = StorageService.load(`${mode.gameId}.letterStart.${letter}`);
            if (!startedAt) return false;
            return StorageService.load(`${mode.gameId}.letter.${letter}`) !== true;
        });
    }

    /** Même logique que `InProgress.scanDaily()`. */
    hasDailyRun(mode) {
        const prefix = DailyChallenge.STORAGE_PREFIXES[mode.mode];
        const today = new Date().toISOString().slice(0, 10);
        if (localStorage.getItem(`${prefix}.${today}`) !== null) return false;

        let data = null;
        try {
            data = JSON.parse(localStorage.getItem(`${prefix}.progress`));
        } catch {
            return false;
        }
        return !!(data && data.date === today && data.currentIndex && data.currentIndex < mode.total);
    }

    /** URL d'une page de jeu, préfixe de langue compris (voir `InProgress.href()`). */
    href(slug) {
        return `${this.lang.currentLang === "en" ? "/en/" : "/"}${slug}`;
    }

    /**
     * Le « i » des scores non mesurés déplie une note commune sous le tableau plutôt
     * qu'une infobulle : le texte est long, et il doit rester lisible au doigt.
     */
    bindLegacyInfo() {
        const note = this.body.querySelector(".lb-legacy-note");
        if (!note) return;
        // `button.lb-info` seulement : le pictogramme repris en tête de la note est un
        // simple rappel visuel, pas une commande.
        this.body.querySelectorAll("button.lb-info").forEach(btn => {
            btn.addEventListener("click", () => {
                const open = note.hidden;
                note.hidden = !open;
                this.body.querySelectorAll("button.lb-info").forEach(b => b.setAttribute("aria-expanded", String(open)));
                if (open) note.scrollIntoView({ behavior: "smooth", block: "nearest" });
            });
        });
    }

    /**
     * Bandeau « où j'en suis » : sur des dizaines de lignes, se retrouver à l'œil est
     * pénible, et l'écart avec le joueur du dessus est ce qui donne envie de relancer
     * une partie. Rien n'est affiché pour un visiteur non connecté.
     */
    renderMyStanding(mode, entries) {
        if (!this.me) return "";
        const l = this.lang;
        const index = entries.findIndex(e => e.username === this.me);
        if (index === -1) {
            return `<p class="lb-standing lb-standing--absent">${l.t("leaderboard.notRankedYet")}</p>`;
        }

        const mine = entries[index];
        const position = l.t("leaderboard.yourPosition")
            .replace("{rank}", mine.rank)
            .replace("{total}", entries.length);

        // L'écart n'a de sens que s'il se joue sur le score : au-dessus, à score égal,
        // c'est un meilleur temps ou moins de fautes qu'il faut, pas des points.
        let gap = "";
        const above = entries[index - 1];
        if (above && above.score > mine.score) {
            gap = l.t("leaderboard.gapToNext")
                .replace("{n}", above.score - mine.score)
                .replace("{player}", above.username);
        } else if (above) {
            gap = l.t("leaderboard.gapTiebreak").replace("{player}", above.username);
        }

        return `<p class="lb-standing">
            <span class="lb-standing-position">${position}</span>
            ${gap ? `<span class="lb-standing-gap">${escapeHtml(gap)}</span>` : ""}
            <button type="button" class="lb-standing-btn">${l.t("leaderboard.findMe")}</button>
        </p>`;
    }

    bindMyStanding() {
        const btn = this.body.querySelector(".lb-standing-btn");
        const row = this.body.querySelector("#lb-my-row");
        if (!btn || !row) return;
        btn.addEventListener("click", () => {
            row.scrollIntoView({ behavior: "smooth", block: "center" });
            row.classList.add("is-flash");
            setTimeout(() => row.classList.remove("is-flash"), 1200);
        });
    }

    /**
     * Mini profil joueur : un seul bind, sur `.leaderboard-body` lui-même (jamais
     * remplacé par `innerHTML =`, contrairement au podium/tableau qu'il contient),
     * pour couvrir tout pseudo cliquable qu'un futur rendu y ajouterait.
     */
    initProfilePopup() {
        this.profilePopup = this.page.querySelector(".player-profile-popup");
        this.profileBody = this.page.querySelector(".player-profile-body");
        if (!this.profilePopup || !this.profileBody) return;

        this.body.addEventListener("click", (e) => {
            // Le « i » d'un score non mesuré vit dans la même ligne : sa propre action
            // (déplier la note) ne doit pas être court-circuitée par le clic de la ligne.
            if (e.target.closest(".lb-info")) return;
            const link = e.target.closest("[data-profile-username]");
            if (link) this.openProfile(link.dataset.profileUsername);
        });

        this.profilePopup.querySelector(".player-profile-close")
            .addEventListener("click", () => this.closeProfile());
        this.profilePopup.addEventListener("click", (e) => {
            if (e.target === this.profilePopup) this.closeProfile();
        });
        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape" && this.profilePopup.classList.contains("show")) this.closeProfile();
        });
    }

    async openProfile(username) {
        this.profilePopup.classList.add("show");
        this.profileBody.innerHTML = `<p class="player-profile-loading">${this.lang.t("leaderboard.loading")}</p>`;
        try {
            const res = await fetch(`${API_BASE}/leaderboard/player/${encodeURIComponent(username)}`);
            if (!res.ok) throw new Error(res.status);
            this.renderProfile(await res.json());
        } catch {
            this.profileBody.innerHTML = `<p class="player-profile-error">${this.lang.t("leaderboard.errorLoad")}</p>`;
        }
    }

    closeProfile() {
        this.profilePopup.classList.remove("show");
    }

    /** Rendu délégué à `PlayerProfile.js`, partagé avec la popup du back-office. */
    renderProfile(profile) {
        const l = this.lang;
        const { html, hasStats } = renderProfileStats(profile.games || {}, l, profile.dailyMedals, profile.dailyFallback);
        this.profileBody.innerHTML = `
            ${renderProfileHeader(profile, l)}
            ${hasStats ? html : `<p class="player-profile-empty">${l.t("leaderboard.profileEmpty")}</p>`}
        `;
    }
}
